import { useSyncedHealth } from '../sync/HealthConnection';
import { useState } from 'react';
import { ChartCard } from '../../components/charts/ChartCard';
import { LineChart } from '../../components/charts/LineChart';
import { DateRangeSelector, type DateRange } from '../../components/ui/DateRangeSelector';
import { EmptyState } from '../../components/ui/EmptyState';
import { MetricCard } from '../../components/ui/MetricCard';
import { MetricGrid } from '../../components/ui/MetricGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { StatusBadge, type BadgeTone } from '../../components/ui/StatusBadge';
import { conditions, healthSyncStatus, latestMetrics, painEntries, recoveryPlan, weightTrend } from '../../mock/bodyData';
import type { HealthMetric } from '../../types/health';
import './BodyPage.css';

const ranges: DateRange[] = ['7D', '30D', '3M', '6M', '1Y', 'All'];

export function BodyPage() {
  const synced = useSyncedHealth();
  const [range, setRange] = useState<DateRange>('7D');
  const metric = (type: HealthMetric['type']) => (synced.metrics.length ? synced.metrics.slice().sort((a, b) => b.startDate.localeCompare(a.startDate)) : latestMetrics).find((item) => item.type === type);
  const activeCondition = conditions.find((condition) => condition.status === 'active');
  const activePain = painEntries.find((entry) => entry.conditionId === activeCondition?.id);
  const appleHealthSync = healthSyncStatus.find((sync) => sync.id === 'sync_apple_health');
  const visibleWeightTrend = filterTrendByRange(synced.metrics.length ? synced.metrics.slice().sort((a, b) => a.startDate.localeCompare(b.startDate)).map(m => ({ date: m.startDate.slice(0, 10), value: m.value, unit: m.unit, source: m.source })) : weightTrend, range);

  function formatMetric(type: HealthMetric['type'], precision = 1) {
    const item = metric(type);
    if (!item) return '—';
    return `${item.value.toFixed(precision)} ${item.unit}`;
  }

  function syncTone(status: (typeof healthSyncStatus)[number]['status'] | undefined): BadgeTone {
    if (status === 'connected') return 'good';
    if (status === 'manual') return 'primary';
    return 'neutral';
  }

  return (
    <div className="page-stack">
      <PageHeader eyebrow="BODY" title="Body" subtitle="Apple Health body data appears here only after real iPhone Health import is connected." />

      <MetricGrid>
        <MetricCard label="Weight" value={formatMetric('weight')} metadata={metric('weight') ? 'Apple Health' : 'No Apple Health import yet'} />
        <MetricCard label="Apple Health" value={synced.count ? 'records received' : appleHealthSync?.status ?? 'not connected'} metadata={`${synced.metrics.length ? 1 : appleHealthSync?.metricTypes.length ?? 0} metrics available`} />
      </MetricGrid>

      <section>
        <SectionHeader title="Health connection" subtitle="Body metrics stay useful by carrying injury, pain, and sync context forward." />
        <div className="body-health-grid">
          <article className="body-health-card">
            <div className="body-health-card-header">
              <div>
                <h3>{activeCondition?.name ?? 'No active condition'}</h3>
                <p>{activeCondition?.notes ?? 'Body has no active health limitation attached.'}</p>
              </div>
              <StatusBadge tone={activeCondition ? 'warning' : 'good'}>{activeCondition?.status ?? 'clear'}</StatusBadge>
            </div>
            <dl className="body-health-facts">
              <div>
                <dt>Pain today</dt>
                <dd>{activePain ? `${activePain.painScore}/10 ${activePain.location}` : 'No pain entry'}</dd>
              </div>
              <div>
                <dt>Recommendation</dt>
                <dd>{recoveryPlan.recommendation}</dd>
              </div>
            </dl>
          </article>

          <article className="body-health-card">
            <div className="body-health-card-header">
              <div>
                <h3>Apple Health sync</h3>
                <p>{synced.count ? 'Records received through your private Body API.' : appleHealthSync?.note}</p>
              </div>
              <StatusBadge tone={syncTone(appleHealthSync?.status)}>{synced.count ? 'records received' : appleHealthSync?.status ?? 'not connected'}</StatusBadge>
            </div>
            <dl className="body-health-facts">
              <div>
                <dt>Metrics connected</dt>
                <dd>{synced.metrics.length ? 1 : appleHealthSync?.metricTypes.length ?? 0}</dd>
              </div>
              <div>
                <dt>Website records</dt>
                <dd>{synced.count ? `${synced.count} received` : 'No records received'}</dd>
              </div>
            </dl>
          </article>
        </div>
      </section>

      <ChartCard title="Weight trend" actions={<DateRangeSelector ranges={ranges} selected={range} onSelect={setRange} />}>
        {visibleWeightTrend.length ? (
          <LineChart title={`Weight trend ${range}`} points={visibleWeightTrend} />
        ) : (
          <EmptyState title="No Apple Health weight data yet" description="Weight history will appear after real iPhone Health samples are imported." />
        )}
      </ChartCard>

      <section>
        <SectionHeader title="Measurements" subtitle="Manual measurements are separate from Apple Health metrics." />
        <div className="measurements-grid">
          {['Waist', 'Hip', 'Left Thigh', 'Right Thigh'].map((label) => (
            <MetricCard key={label} label={label} value="—" metadata="No record yet" />
          ))}
        </div>
      </section>
    </div>
  );
}

function filterTrendByRange<T extends { date: string }>(points: T[], selectedRange: DateRange) {
  const rangeDays: Partial<Record<DateRange, number>> = {
    '7D': 7,
    '30D': 30,
    '3M': 92,
    '6M': 183,
    '1Y': 365,
  };
  const days = rangeDays[selectedRange];
  const latest = points.at(-1);

  if (!days || !latest) return points;

  const dayMs = 24 * 60 * 60 * 1000;
  const latestDate = new Date(`${latest.date}T00:00:00`).getTime();
  const startDate = latestDate - (days - 1) * dayMs;

  return points.filter((point) => new Date(`${point.date}T00:00:00`).getTime() >= startDate);
}
